import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicHeader, PublicFooter } from "@/components/layout/Layout";
import { L } from "@/constants/labels";
import useReveal from "@/hooks/useReveal";
import { Hero } from "@/components/landing/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { Metiers } from "@/components/landing/Metiers";
import { Gallery } from "@/components/landing/Gallery";
import { Counters } from "@/components/landing/Counters";
import {
  Building2, HardHat, Search, CalendarDays, FileCheck2, BadgeCheck,
  MessageSquare, ShieldCheck, ArrowUpRight, Check,
  Megaphone, Inbox, MessagesSquare, UserPlus, MapPin, MousePointerClick, Receipt,
} from "lucide-react";

const Pill = ({ Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#1B3A5C]/12 text-[11px] font-medium text-[#0F2338] font-body-l">
    <Icon size={12} className="text-[#1B3A5C]" /> {children}
  </span>
);

const Reveal = ({ as: Tag = "div", delay = 0, className = "", children, ...rest }) => {
  const ref = useReveal();
  const delayClass = delay > 0 ? ` reveal-delay-${delay}` : "";
  return (
    <Tag ref={ref} className={`reveal${delayClass} ${className}`} {...rest}>
      {children}
    </Tag>
  );
};

const CREAM = "#FAFAF7";
const CREAM_SOFT = "#F5F3EE";

export default function Landing() {
  return (
    <div className="min-h-screen font-body-l" style={{ backgroundColor: CREAM }}>
      <Helmet>
        <title>VosCompagnons — Prestataires du bâtiment vérifiés | Entreprises, syndics, gestionnaires, agences, assureurs</title>
        <meta name="description" content="Plateforme B2B de mise en relation dans le bâtiment. Trouvez des sous-traitants indépendants, disponibles et vérifiés, sur votre secteur. Attestations décennale, RC Pro, URSSAF consultables avant tout engagement." />
        <link rel="canonical" href="https://voscompagnons.fr/" />
        <meta property="og:title" content="VosCompagnons — Trouvez le bon partenaire, chantier par chantier" />
        <meta property="og:description" content="Artisans et entreprises du bâtiment se trouvent par métier, par secteur et par disponibilité. Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://voscompagnons.fr/" />
        <meta property="og:locale" content="fr_FR" />
      </Helmet>

      <PublicHeader overlay />
      <Hero />
      <Marquee />
      <Counters />

      {/* =================== NOS DEUX ESPACES =================== */}
      <section style={{ backgroundColor: CREAM }} className="relative">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <Reveal className="mb-14 md:mb-20 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-display font-semibold text-[#0F2338] tracking-[-0.02em]">
              Deux espaces, un même terrain.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8">
            {/* Donneur d'ordre */}
            <Reveal>
              <article
                className="relative bg-white border border-[#E4E7EB] rounded-[8px] p-7 md:p-10 hover:border-[#1B3A5C] hover:-translate-y-1 transition-all duration-500"
                data-testid="target-entreprise"
              >
                <div className="w-12 h-12 rounded-[4px] border border-[#1B3A5C] text-[#1B3A5C] flex items-center justify-center mb-8">
                  <Building2 size={22} strokeWidth={2} />
                </div>
                <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B3A5C]">
                  Donneur d'ordre
                </span>
                <h3 className="mt-3 text-xl leading-[1.2] sm:text-2xl md:text-3xl font-display font-semibold text-[#0F2338] tracking-[-0.01em]">
                  Des sous-traitants disponibles et vérifiés, sur votre secteur.
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#3E4C59] leading-relaxed">
                  Entreprises générales du bâtiment, syndics de copropriété, gestionnaires de patrimoine, agences immobilières, experts en assurance et assureurs.
                </p>
                <div className="mt-6 flex flex-wrap gap-1.5">
                  <Pill Icon={Search}>Trouvez des sous-traitants</Pill>
                  <Pill Icon={FileCheck2}>Documents vérifiés</Pill>
                  <Pill Icon={CalendarDays}>Disponibilités en temps réel</Pill>
                  <Pill Icon={MessageSquare}>Une conversation par chantier</Pill>
                  <Pill Icon={BadgeCheck}>Avis entre pros</Pill>
                </div>
                <div className="mt-10">
                  <Link
                    to="/inscription?role=entreprise"
                    className="inline-flex items-center gap-2 text-[#1B3A5C] font-bold text-base hover:text-[#E8710A] transition-colors group"
                    data-testid="cta-entreprise"
                  >
                    {L.wording.landingCarteEntreprisePublier}
                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>
              </article>
            </Reveal>

            {/* Prestataire — ordre inversé sur mobile pour cibler l'artisan QR-code */}
            <Reveal delay={2} className="order-first md:order-none">
              <article
                className="relative bg-white border border-[#E4E7EB] rounded-[8px] p-7 md:p-10 hover:border-[#E8710A] hover:-translate-y-1 transition-all duration-500"
                data-testid="target-prestataire"
              >
                <div className="w-12 h-12 rounded-[4px] border border-[#E8710A] text-[#E8710A] flex items-center justify-center mb-8">
                  <HardHat size={22} strokeWidth={2} />
                </div>
                <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#E8710A]">
                  {L.wording.landingCarteArtisanTag}
                </span>
                <h3 className="mt-3 text-xl leading-[1.2] sm:text-2xl md:text-3xl font-display font-semibold text-[#0F2338] tracking-[-0.01em]">
                  Des chantiers près de chez vous.
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#3E4C59] leading-relaxed">
                  Artisans, auto-entrepreneurs, micro-entrepreneurs.
                </p>
                <div className="mt-6 flex flex-wrap gap-1.5">
                  <Pill Icon={CalendarDays}>Vos disponibilités</Pill>
                  <Pill Icon={BadgeCheck}>Votre profil en ligne</Pill>
                  <Pill Icon={Search}>Chantiers ouverts</Pill>
                  <Pill Icon={FileCheck2}>Documents à jour</Pill>
                  <Pill Icon={ShieldCheck}>Facturation Factur-X</Pill>
                </div>
                <div className="mt-10">
                  <Link
                    to="/inscription?role=prestataire"
                    className="inline-flex items-center gap-2 text-[#E8710A] font-bold text-base hover:text-[#1B3A5C] transition-colors group"
                    data-testid="cta-prestataire"
                  >
                    Candidater à des chantiers
                    <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

            <Metiers />

      {/* =================== COMMENT ÇA MARCHE =================== */}
      <section style={{ backgroundColor: CREAM_SOFT }} className="relative">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <Reveal className="mb-14 md:mb-20 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-display font-semibold text-[#0F2338] tracking-[-0.02em]">
              Comment ça marche.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8">
            {/* Donneur d'ordre */}
            <Reveal>
              <div
                className="bg-white border border-[#E4E7EB] rounded-[8px] p-6 md:p-10 h-full"
                data-testid="how-entreprise"
              >
                <div className="flex items-center gap-3 mb-10">
                  <div className="w-10 h-10 rounded-[4px] border border-[#1B3A5C] text-[#1B3A5C] flex items-center justify-center">
                    <Building2 size={18} strokeWidth={2} />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B3A5C]">
                    Donneur d'ordre
                  </span>
                </div>
                <ol className="space-y-7">
                  {[
                    { Icon: Megaphone, t: L.wording.landingHowStep1Title, d: "chantier, spécialité, dates, secteur." },
                    { Icon: Inbox, t: "Recevez les candidatures", d: "sous-traitants disponibles, dont les documents administratifs sont déposés sur la plateforme." },
                    { Icon: ShieldCheck, t: "Consultez les attestations avant tout engagement", d: "décennale, RC Pro, URSSAF, sans avoir à les réclamer." },
                    { Icon: MessagesSquare, t: "Échangez, confirmez", d: "une conversation par chantier." },
                  ].map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="shrink-0 flex flex-col items-center gap-1.5">
                        <div
                          className="w-9 h-9 md:w-10 md:h-10 rounded-[4px] border border-[#1B3A5C] text-[#1B3A5C] flex items-center justify-center"
                          data-testid={`how-entreprise-icon-${i + 1}`}
                        >
                          <s.Icon size={17} strokeWidth={1.75} />
                        </div>
                        <span className="text-[10px] font-mono text-[#1B3A5C] tabular-nums font-bold">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="pt-1">
                        <div className="text-base md:text-lg font-bold text-[#1F2933] leading-snug">{s.t}</div>
                        <div className="mt-1.5 text-sm md:text-base text-[#3E4C59] leading-relaxed">{s.d}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>

            {/* Prestataire — ordre inversé sur mobile (cohérence avec les cartes cibles :
                un artisan QR-code voit son parcours avant celui du donneur d'ordre) */}
            <Reveal delay={2} className="order-first md:order-none">
              <div
                className="bg-white border border-[#E4E7EB] rounded-[8px] p-6 md:p-10 h-full"
                data-testid="how-prestataire"
              >
                <div className="flex items-center gap-3 mb-10">
                  <div className="w-10 h-10 rounded-[4px] border border-[#E8710A] text-[#E8710A] flex items-center justify-center">
                    <HardHat size={18} strokeWidth={2} />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#E8710A]">
                    {L.wording.landingHowPrestataireTag}
                  </span>
                </div>
                <ol className="space-y-7">
                  {[
                    { Icon: UserPlus, t: "Créez votre profil", d: "spécialité, secteur d'intervention, disponibilités." },
                    { Icon: MapPin, t: "Recevez les chantiers de votre secteur", d: "près de chez vous, correspondant à votre profil." },
                    { Icon: MousePointerClick, t: "Candidatez en un clic", d: "vos attestations sont déjà là, vous ne les renvoyez plus à chaque chantier." },
                    { Icon: Receipt, t: "Suivez vos missions", d: "factures Factur-X, avis entre pros." },
                  ].map((s, i) => (
                    <li key={i} className="flex gap-4">
                      <div className="shrink-0 flex flex-col items-center gap-1.5">
                        <div
                          className="w-9 h-9 md:w-10 md:h-10 rounded-[4px] border border-[#E8710A] text-[#E8710A] flex items-center justify-center"
                          data-testid={`how-prestataire-icon-${i + 1}`}
                        >
                          <s.Icon size={17} strokeWidth={1.75} />
                        </div>
                        <span className="text-[10px] font-mono text-[#E8710A] tabular-nums font-bold">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                      </div>
                      <div className="pt-1">
                        <div className="text-base md:text-lg font-bold text-[#1F2933] leading-snug">{s.t}</div>
                        <div className="mt-1.5 text-sm md:text-base text-[#3E4C59] leading-relaxed">{s.d}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

            <Gallery />

      {/* =================== NOTRE PARTI PRIS =================== */}
      <section style={{ backgroundColor: CREAM }} className="relative">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-start">
            <Reveal className="md:col-span-8">
              <h2
                className="text-xl leading-[1.25] sm:text-2xl md:text-3xl lg:text-4xl font-display font-semibold text-[#0F2338] tracking-[-0.01em]"
                data-testid="pitch-title"
              >
                De professionnel à professionnel, trouver le bon partenaire ne devrait plus être une difficulté.
              </h2>
            </Reveal>
            <Reveal delay={2} className="md:col-span-4 md:pt-6" data-testid="pitch-body">
              <p className="text-base md:text-lg text-[#3E4C59] leading-relaxed">
                VosCompagnons réunit des professionnels indépendants du bâtiment avec leur spécialité, leur zone d'intervention, leurs disponibilités réelles. Les documents administratifs sont déposés sur la plateforme, consultables et téléchargeables avant tout engagement.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* =================== VOTRE ESPACE =================== */}
      <section style={{ backgroundColor: CREAM_SOFT }} className="relative">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <div className="max-w-3xl mx-auto" data-testid="pricing-grid">
            <ol
              className="bg-white border border-[#E4E7EB] rounded-[4px] divide-y divide-[#E4E7EB] overflow-hidden"
              data-testid="pricing-abonnement"
            >
              {[
                "Recherche et filtres illimités",
                L.wording.landingSuiviDocs,
                L.wording.landingPublicationChantiers,
                "Messagerie directe",
                "Avis entre professionnels",
              ].map((f, i) => (
                <Reveal
                  as="li"
                  key={f}
                  delay={Math.min(i + 1, 4)}
                  className="flex items-center gap-5 md:gap-7 px-6 md:px-8 py-5 md:py-6 hover:bg-[#FAFAF7] transition-colors group"
                >
                  <div className="w-9 h-9 rounded-[4px] border border-[#1B3A5C] text-[#1B3A5C] flex items-center justify-center shrink-0 group-hover:bg-[#1B3A5C] group-hover:text-white transition-colors">
                    <Check size={18} strokeWidth={2.5} />
                  </div>
                  <span className="text-base md:text-lg font-semibold text-[#1F2933]">{f}</span>
                </Reveal>
              ))}
            </ol>

            <Reveal className="mt-8 flex justify-center">
              <Link
                to="/inscription?role=entreprise"
                className="btn-primary min-h-[56px] px-8 w-full sm:w-auto text-base group"
                data-testid="pricing-abonnement-cta"
              >
                Créer mon compte entreprise
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* =================== FINAL CTA =================== */}
      <section className="relative bg-[#0F2338] text-white overflow-hidden grain">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20 md:py-32">
          <Reveal>
            <span className="text-[11px] md:text-xs uppercase tracking-[0.24em] font-bold text-white/70">
              Prêt à démarrer
            </span>
          </Reveal>

          <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
            <Reveal delay={1} className="md:col-span-8">
              <h2 className="text-2xl leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-display font-semibold tracking-[-0.02em]">
                Prêt à professionnaliser vos chantiers ?
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/60">
                Compte créé en 2 minutes — SIRET requis.
              </p>
            </Reveal>
            <Reveal delay={3} className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3">
              <Link
                to="/inscription"
                className="btn-accent min-h-[56px] w-full px-6 text-base group"
                data-testid="footer-cta-register"
              >
                Créer mon compte
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/connexion"
                className="btn min-h-[56px] w-full px-6 text-base bg-transparent text-white border border-white/40 hover:bg-white hover:text-[#1F2933]"
                data-testid="footer-cta-login"
              >
                J'ai déjà un compte
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

            <PublicFooter />
    </div>
  );
}
